export const PAYMENT_STATUS_TYPE = {
  CAUTION: 'caution',
  INFO: 'info',
  IN_PROGRESS: 'in_progress',
  NEUTRAL: 'neutral',
  SUCCESS: 'success',
  WARN: 'warn',
} as const;

type PaymentError = {
  code: string;
  // Returned by Metroid backend
  code_details?: string;
  // Returned by RMAPI backend
  message?: string;
};

export const PAYMENT_PHASE = {
  SELECT_PAYMENT_METHOD: 'select payment method',
  CREDIT_CARD_CAPTURE: 'credit card capture',
  CREDIT_CARD_CHARGE: 'credit card charge',
  PUBLISH_LISTING: 'publish listing',
  CAUGHT_ERROR: 'caught error',
};

export const PAYMENT_METHOD = {
  NEW: 'new',
  DEFAULT: 'default',
};

export const ERROR_CODES = {
  // Returned by Metroid backend
  ZE_UI: 'ZE-UI',
  // Returned by RMAPI backend
  CANNOT_MAKE_PAYMENTS: 'CANNOT_MAKE_PAYMENTS',
  METROID_MAINTENANCE_MODE: 'METROID_MAINTENANCE_MODE',
};

export const ERROR_MESSAGES = {
  // Form validation errors
  EXPIRATION_PAST: 'Expiration date must be a future date.',
  BAD_ZIP: 'Enter a valid ZIP code.',
  INVALID_CARD_NUMBER: 'Invalid credit card number',

  // Service errors
  METROID_MAINTENANCE_MODE:
    'The payment service is currently down for maintenance. We’ll be back up soon. Thank you for your patience.',
  CANNOT_MAKE_PAYMENTS: 'You cannot make payments for this listing',

  // Rate limiting and attempt errors
  MAX_ATTEMPTS_ERROR:
    'Transaction failed. You have had too many unsuccessful attempts, please check your information and try again later.',
  SUBMIT_TOO_QUICK: 'Too many attempts. Please wait a moment before trying again.',

  // Gateway transaction errors (payment processor declines)
  GATEWAY_TRANSACTION_ERROR: 'We couldn’t process your payment. Check your card details or try a different card.',
  TRANSACTION_DECLINED: 'We couldn’t process your payment. Check your card details or try a different card.',

  // Specific decline reasons
  CALL_VOICE_CENTER:
    'We couldn’t add your payment method. Check that your card details are correct, or try a different card. If the issue continues, contact your bank.',
  CUSTOMER_NOT_AUTHORIZED: 'Please use a different payment method or contact support',

  // Form validation errors
  HOSTED_PAGE_FIELD_VALIDATION_ERROR: 'Please check that all required fields are filled out correctly',
  INVALID_VALUES: 'Some information you entered is invalid. Please review your payment details and try again',
  INVALID_TIMEOUT: 'Your session timed out. Please refresh the page and try again.',
  RECAPTCHA_VALIDATION_FAILED: 'Security verification failed. Please refresh the page and try again.',

  // Default fallback
  DEFAULT: 'Unable to process payment. Please check your information and try again.',
  TIMED_OUT: 'Something went wrong. Try again later.',
};

/**
 * Regex patterns for parsing code_details field
 * Based on Metroid documentation: https://zillowgroup.atlassian.net/wiki/spaces/zproddev/pages/273287794/Metroid+Purchase+Path+Onboarding#Handle-ZE-UI-Submit-Failures
 *
 * ZE-UI code_details follow the format: [failure_reason] failure_message
 * where failure_reason is one of:
 * - GatewayTransactionError (most common)
 * - Submit_Too_Quick
 * - HostedPageFieldValidationError
 * - Invalid_Values
 * - Invalid_TimeOut
 * - Attempt_Exceed_Limitation
 * - ReCaptcha_Validation_Failed
 */
const ERROR_REGEX = {
  // Form validation errors
  EXPIRATION_PAST: /Expiration date must be a future date/i,
  ZIP_CODE: /ZIP code/i,
  INVALID_CARD_NUMBER: /Invalid CC Number/i,

  // ZE-UI failure reasons (bracketed format from Metroid docs)
  GATEWAY_TRANSACTION_ERROR: /\[GatewayTransactionError\]/i,
  ATTEMPTS_EXCEEDED: /\[?Attempt_Exceed_Limitation\]?/i,
  SUBMIT_TOO_QUICK: /\[?Submit_Too_Quick\]?/i,
  HOSTED_PAGE_FIELD_VALIDATION_ERROR: /\[HostedPageFieldValidationError\]/i,
  INVALID_VALUES: /\[Invalid_Values\]/i,
  INVALID_TIMEOUT: /\[Invalid_TimeOut\]/i,
  RECAPTCHA_VALIDATION_FAILED: /\[ReCaptcha_Validation_Failed\]/i,

  // Additional error patterns (backward compatibility with older formats)
  TRANSACTION_DECLINED: /Transaction declined/i,
  CALL_VOICE_CENTER: /Call Voice Center/i,
  CUSTOMER_NOT_AUTHORIZED: /Customer Advises Not Authorized/i,
  DOWNSTREAM: /Downstream/i,
};

/**
 * Maps payment error to user-friendly message based on code and code_details
 *
 * ZE-UI errors should be parsed based on the code_details field which follows the format:
 * [failure_reason] failure_message
 *
 * @param error - The error object with code, code_details, and optional message
 * @returns User-friendly error message
 */
export const mapPaymentError = (error: PaymentError): string => {
  if (!error) {
    return ERROR_MESSAGES.DEFAULT;
  }

  const { code, code_details } = error;

  // Handle specific error codes first (non ZE-UI codes)
  if (code === ERROR_CODES.METROID_MAINTENANCE_MODE) {
    return ERROR_MESSAGES.METROID_MAINTENANCE_MODE;
  }

  if (code === ERROR_CODES.CANNOT_MAKE_PAYMENTS) {
    return ERROR_MESSAGES.CANNOT_MAKE_PAYMENTS;
  }

  // Handle ZE-UI errors by examining code_details
  // Check in order of frequency (most common first) based on Metroid documentation
  if (code === ERROR_CODES.ZE_UI && code_details) {
    // 1. GatewayTransactionError (most common) - payment gateway declines
    // Example: "[GatewayTransactionError] Transaction declined.89 - Credit Floor"
    if (ERROR_REGEX.GATEWAY_TRANSACTION_ERROR.test(code_details)) {
      const message = ERROR_MESSAGES.GATEWAY_TRANSACTION_ERROR;
      return message;
    }

    // 2. Submit_Too_Quick - rate limiting
    if (ERROR_REGEX.SUBMIT_TOO_QUICK.test(code_details)) {
      const message = ERROR_MESSAGES.SUBMIT_TOO_QUICK;
      return message;
    }

    // 3. HostedPageFieldValidationError - field validation errors
    if (ERROR_REGEX.HOSTED_PAGE_FIELD_VALIDATION_ERROR.test(code_details)) {
      const message = ERROR_MESSAGES.HOSTED_PAGE_FIELD_VALIDATION_ERROR;
      return message;
    }

    // 4. Invalid_Values - invalid field values
    if (ERROR_REGEX.INVALID_VALUES.test(code_details)) {
      const message = ERROR_MESSAGES.INVALID_VALUES;
      return message;
    }

    // 5. Invalid_TimeOut - timestamp validation failure
    if (ERROR_REGEX.INVALID_TIMEOUT.test(code_details)) {
      const message = ERROR_MESSAGES.INVALID_TIMEOUT;
      return message;
    }

    // 6. Attempt_Exceed_Limitation - max attempts exceeded
    if (ERROR_REGEX.ATTEMPTS_EXCEEDED.test(code_details)) {
      const message = ERROR_MESSAGES.MAX_ATTEMPTS_ERROR;
      return message;
    }

    // 7. ReCaptcha_Validation_Failed - reCAPTCHA failure
    if (ERROR_REGEX.RECAPTCHA_VALIDATION_FAILED.test(code_details)) {
      const message = ERROR_MESSAGES.RECAPTCHA_VALIDATION_FAILED;
      return message;
    }

    // Legacy/specific patterns for backward compatibility
    // These handle cases where the bracketed format might not be present
    if (ERROR_REGEX.CUSTOMER_NOT_AUTHORIZED.test(code_details)) {
      const message = ERROR_MESSAGES.CUSTOMER_NOT_AUTHORIZED;
      return message;
    }

    if (ERROR_REGEX.CALL_VOICE_CENTER.test(code_details)) {
      const message = ERROR_MESSAGES.CALL_VOICE_CENTER;
      return message;
    }

    if (ERROR_REGEX.TRANSACTION_DECLINED.test(code_details)) {
      const message = ERROR_MESSAGES.TRANSACTION_DECLINED;
      return message;
    }
  }

  // escape for timedout
  if (error?.message?.includes('Request timed out:')) {
    return ERROR_MESSAGES.TIMED_OUT;
  }

  // If error has a message property, use it
  if (error.message) {
    return error.message;
  }

  // Default error message
  const defaultMessage = ERROR_MESSAGES.DEFAULT;
  return defaultMessage;
};

/**
 * Maps payment form error to user-friendly message based on code_details
 * Returns empty string if not a form-specific error that should be shown inline
 *
 * These are validation errors that should be displayed near the form fields
 * rather than as general error messages. These are typically caught before submission
 * but can also come from server-side validation
 *
 * @param error - The error object with code, code_details, and optional message
 * @returns Form-specific error message or empty string
 */
export const mapPaymentFormError = (error: PaymentError): string => {
  if (!error || error.code !== ERROR_CODES.ZE_UI || !error.code_details) {
    return '';
  }

  const { code_details } = error;

  if (ERROR_REGEX.EXPIRATION_PAST.test(code_details)) {
    const message = ERROR_MESSAGES.EXPIRATION_PAST;
    return message;
  }

  if (ERROR_REGEX.ZIP_CODE.test(code_details)) {
    const message = ERROR_MESSAGES.BAD_ZIP;
    return message;
  }

  if (ERROR_REGEX.INVALID_CARD_NUMBER.test(code_details)) {
    const message = ERROR_MESSAGES.INVALID_CARD_NUMBER;
    return message;
  }

  // HostedPageFieldValidationError can contain specific field errors
  // Example: "[HostedPageFieldValidationError] creditCardHolderName:ExceededMaxLength"
  if (ERROR_REGEX.HOSTED_PAGE_FIELD_VALIDATION_ERROR.test(code_details)) {
    const message = ERROR_MESSAGES.HOSTED_PAGE_FIELD_VALIDATION_ERROR;
    return message;
  }

  // If it's not a form-specific error, return empty string
  return '';
};
